import { create } from 'zustand';

import { api } from '../api/client';
import { useSessionStore } from './session';

/**
 * 오늘 토마토 찾기 — 앱 전역 상태. 하루 3개를 한 번에 하나씩 순차로 찾는다.
 * 아이콘이 글 카드·헤더·푸터 어디에도 뜰 수 있어(여러 화면이 같은 상태를 봄) 세션과 별도 스토어.
 *
 * targetElementId = 지금 찾아야 할 "다음" 위치. 하나 찾으면 서버가 그다음 위치를 돌려주고,
 * 다 찾으면 null. 찾을 때마다 세션 잔액을 refreshMe로 갱신해 헤더 배지에 반영한다.
 */

interface HuntState {
  total: number;
  foundCount: number;
  remaining: number;
  allFound: boolean;
  elementId: string | null;
  finderCount: number;
}

interface ClaimResponse extends HuntState {
  ok: boolean;
  found: boolean;
  newlyAwarded?: boolean;
  reward?: number;
}

interface TomatoHuntState {
  loaded: boolean;
  total: number;
  remaining: number;
  foundCount: number;
  finderCount: number;
  /** 지금 찾아야 할 다음 위치. null이면 안 뜬다(미로딩·다 찾음). */
  targetElementId: string | null;
  /** 클릭 처리 중(중복 클릭 방지). */
  claiming: boolean;
  /** 방금 찾았을 때 축하 토스트에 쓸 값. 닫으면 null. */
  celebration: {
    remaining: number;
    allFound: boolean;
    reward: number;
    finderCount: number;
  } | null;
  load: () => Promise<void>;
  claim: (elementId: string) => Promise<void>;
  dismissCelebration: () => void;
}

export const useTomatoHuntStore = create<TomatoHuntState>((set, get) => ({
  loaded: false,
  total: 0,
  remaining: 0,
  foundCount: 0,
  finderCount: 0,
  targetElementId: null,
  claiming: false,
  celebration: null,

  load: async () => {
    try {
      const res = await api.get<HuntState>('/api/tomato-hunt/today');
      set({
        loaded: true,
        total: res.total,
        remaining: res.remaining,
        foundCount: res.foundCount,
        finderCount: res.finderCount,
        targetElementId: res.elementId,
      });
    } catch {
      // 온보딩 전(403)·네트워크 등은 조용히 무시 — 게임을 안 띄우면 그만이다.
    }
  },

  claim: async (elementId) => {
    const { claiming, targetElementId } = get();
    if (claiming || !targetElementId) return;
    set({ claiming: true });
    try {
      const res = await api.post<ClaimResponse>('/api/tomato-hunt/claim', { elementId });
      // 상태는 항상 서버 응답으로 동기화(다음 위치·남은 개수).
      set({
        total: res.total,
        remaining: res.remaining,
        foundCount: res.foundCount,
        finderCount: res.finderCount,
        targetElementId: res.elementId,
      });
      // 새로 지급됐을 때만 축하 토스트.
      if (res.found && res.newlyAwarded) {
        set({
          celebration: {
            remaining: res.remaining,
            allFound: res.allFound,
            reward: res.reward ?? 0,
            finderCount: res.finderCount,
          },
        });
        void useSessionStore.getState().refreshMe();
      }
    } catch {
      /* 실패는 조용히 — 다음 로드 때 상태가 맞춰진다. */
    } finally {
      set({ claiming: false });
    }
  },

  dismissCelebration: () => set({ celebration: null }),
}));
